/** The shell every page renders inside: masthead, page, footer. */

import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';

import { LISTING_LIFETIME_DAYS } from '@bidtohire/shared';

import { PUBLISH_FROM_LABEL } from '../lib/bid-pricing';

import { useAuth } from '../lib/auth-context';
import { FeedbackDialog } from './FeedbackDialog';
import { BrandGlyph, CloseIcon, MenuIcon } from './ui';

/** The masthead links, in the artboard's order. */
const NAV: readonly { readonly to: string; readonly label: string }[] = [
  { to: '/', label: 'Leaderboard' },
  { to: '/categories', label: 'Categories' },
  { to: '/about', label: 'How ranking works' },
  { to: '/for-employers', label: 'For employers' },
];

function Wordmark(): ReactNode {
  return (
    <Link className="brand" to="/">
      <BrandGlyph />
      <span>
        bidtohire<span className="tld">.me</span>
      </span>
    </Link>
  );
}

export function Layout(): ReactNode {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  // A route change closes the drawer; leaving it open across a navigation is
  // the classic way a mobile menu ends up covering the page you asked for.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // React Router does not scroll to a hash of its own accord, so without this a
  // link to a policy section lands wherever the previous page was scrolled to.
  useEffect(() => {
    const id = location.hash.slice(1);
    const target = id === '' ? null : document.getElementById(id);
    if (target !== null) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  // Escape closes it, as it does every other layer in the product.
  useEffect(() => {
    if (!drawerOpen) return;
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') setDrawerOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  // Nothing scrolls behind the open menu. The class carries the rule so the
  // lock stays in the stylesheet with the drawer it belongs to.
  useEffect(() => {
    if (!drawerOpen) return;
    document.documentElement.classList.add('drawer-open');
    return () => document.documentElement.classList.remove('drawer-open');
  }, [drawerOpen]);

  function signOut(): void {
    auth.signOut();
    void navigate('/');
  }

  return (
    <div className="page">
      <header className="masthead">
        <div className="inner">
          <Wordmark />

          <nav className="mastnav" aria-label="Main">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => (isActive ? 'is-active' : undefined)}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <span className="spacer" />

          <div className="mastactions">
            {auth.isSignedIn ? (
              <>
                <NavLink to="/dashboard">Dashboard</NavLink>
                <button type="button" className="linkbtn" onClick={signOut}>
                  Sign out
                </button>
              </>
            ) : (
              <NavLink to="/auth">Sign in</NavLink>
            )}
            <Link className="pill pill-primary pill-sm" to="/post">
              Post a job — {PUBLISH_FROM_LABEL}
            </Link>
          </div>

          <button
            type="button"
            className="mastburger"
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
            aria-controls="mastdrawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
          >
            {drawerOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>

        <div id="mastdrawer" className={drawerOpen ? 'mastdrawer is-open' : 'mastdrawer'}>
          <div className="inner">
            {NAV.map((item) => (
              <Link key={item.to} to={item.to}>
                {item.label}
              </Link>
            ))}
            {auth.isSignedIn ? (
              <>
                <Link to="/dashboard">Dashboard</Link>
                <button type="button" className="linkbtn" onClick={signOut}>
                  Sign out
                </button>
              </>
            ) : (
              <Link to="/auth">Sign in</Link>
            )}
            <Link className="pill pill-primary" to="/post">
              Post a job — {PUBLISH_FROM_LABEL}
            </Link>
          </div>
        </div>
      </header>

      {/* The scrim: it dims the page under the menu and gives a tap anywhere
          off the menu the obvious meaning. It stays out of the accessibility
          tree and out of the tab order — the hamburger already reads as the
          close control, and Escape already does the job from the keyboard, so
          announcing this as a second "Close menu" button would only be a
          duplicate to step past. */}
      {drawerOpen ? (
        <button
          type="button"
          className="mastscrim"
          aria-hidden="true"
          tabIndex={-1}
          onClick={() => setDrawerOpen(false)}
        />
      ) : null}

      <main className="site-main">
        <Outlet />
      </main>

      <footer className="site-footer">
        <div className="inner">
          <span>
            © {new Date().getFullYear()} bidtohire.me · Listings run {LISTING_LIFETIME_DAYS} days
            and renew free · Payments by Stripe
          </span>
          <div className="footer-links">
            {/* One name per page, and a policy link that reaches the policy:
                the refund terms are section 05 of the Terms, not the explainer. */}
            <Link to="/about">How ranking works</Link>
            <Link to="/terms#payments">Refund policy</Link>
            <Link to="/for-employers">For employers</Link>
            <Link to="/post">Post a job</Link>
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            {/* The only feedback entry point in the product, on purpose. */}
            <button type="button" className="linkbtn" onClick={() => setFeedbackOpen(true)}>
              Feedback
            </button>
          </div>
        </div>
      </footer>

      {feedbackOpen ? <FeedbackDialog onClose={() => setFeedbackOpen(false)} /> : null}
    </div>
  );
}
